import { apiClient } from '@/lib/api-client';
import type {
  IncidentDetail,
  IncidentList,
  Monitor,
  MonitorDetail,
  MonitorList,
  MonitoringOptions,
  NotificationTarget,
  NotificationTargetList,
} from '@/types/api';

export const MONITORS_KEY = ['monitoring', 'monitors'] as const;
export const INCIDENTS_KEY = ['monitoring', 'incidents'] as const;
export const NOTIFICATION_TARGETS_KEY = ['monitoring', 'notification-targets'] as const;
export const MONITORING_OPTIONS_KEY = ['monitoring', 'options'] as const;

export const monitorKey = (id: string) => ['monitoring', 'monitors', id] as const;
export const incidentKey = (id: string) => ['monitoring', 'incidents', id] as const;

export interface MonitorInput {
  name: string;
  target: string;
  interval_seconds: number;
  timeout_seconds: number;
  expect_status_from: number;
  expect_status_to: number;
  expect_body_contains: string;
  /** Days of headroom an SSL or domain-expiry monitor requires. Ignored by the other kinds. */
  expect_warn_days?: number;
  /** DNS record type to look up, uppercase. Empty for every other kind. */
  expect_record_type?: string;
  /** The value a DNS answer must contain. Empty for every other kind. */
  expect_value?: string;
  /**
   * Which server this monitor is about.
   *
   * Required for a server monitor, where it is also the target. Optional elsewhere, and what lets
   * the platform fix things: without it there is nowhere for a fix to run.
   */
  server_id?: string;
  /** Whether the platform may try the safe fixes when this goes down. Defaults to on. */
  auto_fix?: boolean;
  /** Server monitors only. 0 switches a check off. */
  threshold_disk?: number;
  threshold_memory?: number;
  threshold_load?: number;
  threshold_cpu?: number;
}

export const monitoringApi = {
  /**
   * The limits the form needs, read from the server rather than restated here.
   *
   * The interval floor and the confirmation count belong to the domain. Duplicating them in
   * TypeScript would put two copies of the same rule in the codebase, and they would drift the
   * first time either changed.
   */
  options: () => apiClient.get<MonitoringOptions>('/monitoring/options'),

  list: () => apiClient.get<MonitorList>('/monitoring/monitors'),

  get: (id: string, windowDays = 7) =>
    apiClient.get<MonitorDetail>(`/monitoring/monitors/${id}?days=${windowDays}`),

  create: (input: MonitorInput & { kind: string }) =>
    apiClient.post<Monitor>('/monitoring/monitors', input),

  /**
   * Switches automatic fixes on or off, and links the server they may run on.
   *
   * Separate from `update` so a control that only knows a monitor's summary cannot rewrite an
   * expectation it was never shown. An empty `server_id` unlinks; a server monitor's server is its
   * target and is left alone whatever is sent.
   */
  setAutoFix: (id: string, input: { auto_fix: boolean; server_id: string }) =>
    apiClient.patch<Monitor>(`/monitoring/monitors/${id}/auto-fix`, input),

  update: (id: string, input: MonitorInput) =>
    apiClient.put<Monitor>(`/monitoring/monitors/${id}`, input),

  /**
   * Runs a check now, synchronously.
   *
   * Bounded by the monitor's own timeout — at most 60 seconds, typically well under one. Somebody
   * who has just created a monitor, or just fixed what it was complaining about, should not have
   * to wait an interval to find out whether they got it right.
   */
  check: (id: string) => apiClient.post<Monitor>(`/monitoring/monitors/${id}/check`, {}),

  pause: (id: string) => apiClient.post<Monitor>(`/monitoring/monitors/${id}/pause`, {}),

  resume: (id: string) => apiClient.post<Monitor>(`/monitoring/monitors/${id}/resume`, {}),

  remove: (id: string) => apiClient.delete<void>(`/monitoring/monitors/${id}`),

  incidents: () => apiClient.get<IncidentList>('/monitoring/incidents'),

  /** One outage and its timeline: every check, fix and message, in order. */
  incident: (id: string) => apiClient.get<IncidentDetail>(`/monitoring/incidents/${id}`),

  acknowledge: (id: string) =>
    apiClient.post<void>(`/monitoring/incidents/${id}/acknowledge`, {}),

  notificationTargets: () =>
    apiClient.get<NotificationTargetList>('/monitoring/notification-targets'),

  addNotificationTarget: (channel: string, address: string) =>
    apiClient.post<NotificationTarget>('/monitoring/notification-targets', { channel, address }),

  setNotificationTargetEnabled: (id: string, enabled: boolean) =>
    apiClient.patch<void>(`/monitoring/notification-targets/${id}`, { enabled }),

  removeNotificationTarget: (id: string) =>
    apiClient.delete<void>(`/monitoring/notification-targets/${id}`),
};
